import { notifyProgress, Timeout } from '@billy/design-system'
import download from 'downloadjs'
import { TFunction } from 'i18next'
import round from 'lodash/round'
import { v4 as uuidv4 } from 'uuid'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { DownloadStatus } from '../../../../../../../types/downloadStatus'
import { BYTES_IN_KILOBYTE } from '../../../../../constants/bytesInKilobyte'
import { FetchSalesReportDownloadDataResponse } from '../../../../../query-api'
import { ensureBlobFromResponse } from './ensureBlobFromResponse'
import { getProgressNotificationPayload } from './getProgressNotificationPayload'

interface DownloadWithProgressProps {
  bytesPerItem?: number
  fetchExportData: () => Promise<FetchSalesReportDownloadDataResponse>
  fileName?: string
  itemCount?: number
  mimeType: string
  notificationId?: NotificationKeys
  onError?: () => void
  onSuccess?: () => void
  simulatedSpeedKBps?: number
  t: TFunction
}

export class DownloadWithProgress {
  private static readonly MAX_TIMEOUT_MS = 45000
  private static readonly PROGRESS_UPDATE_INTERVAL_MS = 1000
  private static readonly MIN_INCREMENT_STEP_MS = 0.01 // show user at least 1% per second
  private static readonly INCREMENT_SLOWDOWN_RATE = 0.5

  private fetchExportData: () => Promise<FetchSalesReportDownloadDataResponse>
  private fileName: string
  private mimeType: string
  private onError?: () => void
  private onSuccess?: () => void
  private t: TFunction

  private itemCount?: number
  private bytesPerItem?: number
  private simulatedSpeedKBps: number

  private progress = 0
  private intervalId?: Timeout
  private notificationUniqueId: string

  constructor({
    fetchExportData,
    fileName,
    mimeType,
    notificationId,
    onError,
    onSuccess,
    t,
    itemCount,
    bytesPerItem,
    simulatedSpeedKBps = 100,
  }: DownloadWithProgressProps) {
    this.bytesPerItem = bytesPerItem
    this.fetchExportData = fetchExportData
    this.itemCount = itemCount
    this.mimeType = mimeType
    this.notificationUniqueId = `${notificationId}-${uuidv4()}`
    this.onError = onError
    this.onSuccess = onSuccess
    this.simulatedSpeedKBps = simulatedSpeedKBps
    this.t = t
    this.fileName = fileName || this.t('sales_report')
  }

  /**
   * Returns how much progress should increase per interval tick.
   * - If `itemCount` and `bytesPerItem` are present, uses size-based estimate.
   * - Otherwise, uses constant-rate simulation (MAX_TIMEOUT_MS).
   */
  private calculateProgressIncrement(): number {
    const { PROGRESS_UPDATE_INTERVAL_MS, MAX_TIMEOUT_MS } = DownloadWithProgress

    if (this.itemCount && this.bytesPerItem) {
      const totalBytes = this.itemCount * this.bytesPerItem
      const totalSeconds = totalBytes / (this.simulatedSpeedKBps * BYTES_IN_KILOBYTE)
      const estimatedTotalTimeMs = totalSeconds * 1000

      return PROGRESS_UPDATE_INTERVAL_MS / estimatedTotalTimeMs
    }

    return PROGRESS_UPDATE_INTERVAL_MS / MAX_TIMEOUT_MS
  }

  private updateNotification(status: DownloadStatus): void {
    const payload = getProgressNotificationPayload({
      id: this.notificationUniqueId,
      status,
      progress: this.progress,
      t: this.t,
    })

    if (notifyProgress.isActive(this.notificationUniqueId)) {
      notifyProgress.update(payload)
    } else {
      notifyProgress(payload)
    }
  }

  private startSimulatedProgress(): void {
    const { INCREMENT_SLOWDOWN_RATE, MIN_INCREMENT_STEP_MS, PROGRESS_UPDATE_INTERVAL_MS } = DownloadWithProgress
    let increment = Math.max(this.calculateProgressIncrement(), MIN_INCREMENT_STEP_MS)

    this.intervalId = setInterval(() => {
      if (this.progress > 0.9) {
        increment *= INCREMENT_SLOWDOWN_RATE
      }

      this.progress = round(this.progress + increment, 3)

      if (this.progress >= 1) {
        this.stopSimulatedProgress()
        this.progress = 1
      } else {
        this.updateNotification('processing')
      }
    }, PROGRESS_UPDATE_INTERVAL_MS)
  }

  private stopSimulatedProgress(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = undefined
    }
  }

  public async start(): Promise<void> {
    try {
      this.startSimulatedProgress()
      this.updateNotification('processing')

      const response = await this.fetchExportData()

      this.stopSimulatedProgress()
      this.progress = 1
      this.updateNotification('success')

      const blob = await ensureBlobFromResponse(response?.data, this.mimeType)
      download(blob, this.fileName)
      this.onSuccess?.()
    } catch {
      this.stopSimulatedProgress()
      this.progress = 1
      this.updateNotification('error')
      this.onError?.()
    }
  }
}
